import { RF_COLOR } from '@/lib/map/markers/rfIcons';
import { rfTargetsToShow, rfVisibilityKey } from '@/lib/map/rfVisibility';
import { useSurvStore } from '@/lib/store/surv';
import { useTargetsStore } from '@/lib/store/targets';
import { useUiStore } from '@/lib/store/ui';
/**
 * Dotted line from each RF-detected drone to its operator (ENG-253), so the
 * pair reads as one association. One LineString per drone that has a decoded
 * operator position.
 */
import type { GeoJSONSource, Map } from 'mapbox-gl';

const SRC = 'src-rf-links';
const LAYER = 'lyr-rf-links';

let lastWasEmpty = false;

export function syncRfLinksLayer(map: Map) {
  const src = map.getSource(SRC) as GeoJSONSource | undefined;
  if (!src) return;
  const features: GeoJSON.Feature[] = [];
  for (const t of rfTargetsToShow()) {
    const op = t.rf?.operator;
    if (!op) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [
          [op.lng, op.lat],
          [t.lng, t.lat],
        ],
      },
      properties: { target_id: t.target_id },
    });
  }
  // Nothing to draw and nothing was drawn: skip the redundant empty setData.
  if (features.length === 0 && lastWasEmpty) return;
  lastWasEmpty = features.length === 0;
  src.setData({ type: 'FeatureCollection', features });
}

export function subscribeRfLinksLayer(map: Map): () => void {
  if (!map.getSource(SRC)) {
    map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
  }
  if (!map.getLayer(LAYER)) {
    map.addLayer({
      id: LAYER,
      type: 'line',
      source: SRC,
      layout: { 'line-cap': 'round' },
      paint: {
        'line-color': RF_COLOR,
        'line-width': 2,
        'line-opacity': 0.85,
        'line-dasharray': [0.5, 2],
      },
    });
  }
  lastWasEmpty = false;
  syncRfLinksLayer(map);
  let rafId: number | null = null;
  const schedule = () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    rafId = requestAnimationFrame(() => {
      rafId = null;
      syncRfLinksLayer(map);
    });
  };
  const unsubT = useTargetsStore.subscribe((s) => s.byId, schedule);
  const unsubUi = useUiStore.subscribe((s) => s.activeContext, schedule);
  const unsubSurv = useSurvStore.subscribe(rfVisibilityKey, schedule);
  return () => {
    if (rafId !== null) cancelAnimationFrame(rafId);
    unsubT();
    unsubUi();
    unsubSurv();
    if (map.getLayer(LAYER)) map.removeLayer(LAYER);
    if (map.getSource(SRC)) map.removeSource(SRC);
  };
}
